import React from 'react'
import { Button, ButtonType, ButtonSize } from '@wallyhealth/web-core'
import { QRCodeSVG } from 'qrcode.react'
import PageTemplate from '../../../components/PageTemplate'
import ArrivalGuide from '../ArrivalGuide'
import { CheckInStatus, CHECK_IN_UI } from '../domain'
import intelService from '../../../services/intelService'
import styles from './index.module.css'

const HeroSection = () => (
  <section className={styles.hero_section}>
    <div className={styles.hero_title}>YOUR UPCOMING APPOINTMENT</div>
    <div className={styles.hero_subtitle}>
      Everything you need before you arrive — location, directions, and check-in.
    </div>
  </section>
)

const buildDirectionsUrl = (address) => {
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const encoded = encodeURIComponent(address)
  return isIos
    ? `maps://?q=${encoded}`
    : `https://maps.google.com/?q=${encoded}`
}

const AppointmentDetails = ({ formattedDate, formattedTime, location, site, onGetDirections }) => (
  <div className={styles.details_group}>
    <div className={styles.detail_block}>
      <div className={styles.detail_label}>WHEN &amp; WHERE</div>
      <div className={styles.detail_value}>{formattedDate} at {formattedTime}</div>
    </div>
    <div className={styles.detail_block}>
      <div className={styles.location_row}>
        <div className={styles.location_text}>
          <a
            href={buildDirectionsUrl(location.address)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.address_link}
            onClick={onGetDirections}
          >
            {location.name}
          </a>
          {site?.name && (
            <div className={styles.host_entity}>Inside {site.name}</div>
          )}
        </div>
      </div>
    </div>
  </div>
)

const CheckInCode = ({ site }) => {
  if (!site?.checkInCode) return null

  return (
    <div className={styles.qr_section}>
      <div className={styles.qr_caption}>Check-in code</div>
      <div className={styles.qr_label}>
        If asked to check in at {site?.name || 'the building'}, use this code.
      </div>
      <div className={styles.qr_code}>
        <QRCodeSVG
          value={site.checkInCode}
          size={160}
          level="M"
        />
      </div>
    </div>
  )
}

const CheckInAction = ({ ui, isCheckedIn, site, onCheckIn, isCheckingIn }) => (
  <section className={styles.action_section}>
    {isCheckedIn
      ? (
        <>
          <div className={styles.button_row}>
            <div className={styles.option_3}>
              <svg className={styles.option_3_check} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{ui.buttonLabel}</span>
            </div>
          </div>
          <CheckInCode site={site} />
        </>
        )
      : (
        <div className={styles.button_row}>
          <Button
            type={ButtonType.DEFAULT}
            size={ButtonSize.LARGE}
            label={ui.buttonLabel}
            onClick={onCheckIn}
            isLoading={isCheckingIn}
            disabled={!ui.buttonEnabled}
          />
          {ui.helperText && (
            <div className={styles.checkin_helper}>
              {ui.helperText}
            </div>
          )}
        </div>
        )}
  </section>
)

const CheckInDetailsPage = ({
  encodedId,
  data,
  onCheckIn,
  isCheckingIn
}) => {
  const {
    formattedDate,
    formattedTime,
    status,
    location,
    site,
    arrivalSteps
  } = data

  const ui = CHECK_IN_UI[status]
  const isCheckedIn = status === CheckInStatus.CHECKED_IN

  const handleCheckIn = async () => {
    if (!ui.buttonEnabled) return

    intelService.recordClick({
      clickName: 'CheckIn',
      viewName: 'CheckInDetailsPage',
      properties: { encodedId }
    })
    try {
      await onCheckIn()
    } catch { }
  }

  const handleGetDirections = () => {
    intelService.recordClick({
      clickName: 'GetDirections',
      viewName: 'CheckInDetailsPage',
      properties: {
        encodedId,
        locationName: location.name
      }
    })
  }

  return (
    <PageTemplate>
      <div className={styles.page}>
        <HeroSection />
        <div className={styles.layout}>
          <div className={styles.left_column}>
            <AppointmentDetails
              formattedDate={formattedDate}
              formattedTime={formattedTime}
              location={location}
              site={site}
              onGetDirections={handleGetDirections}
            />
            <CheckInAction
              ui={ui}
              isCheckedIn={isCheckedIn}
              site={site}
              onCheckIn={handleCheckIn}
              isCheckingIn={isCheckingIn}
            />
          </div>
        </div>
        {arrivalSteps?.length > 0 && (
          <div className={styles.arrival_section}>
            <ArrivalGuide steps={arrivalSteps} />
          </div>
        )}
      </div>
    </PageTemplate>
  )
}

export default CheckInDetailsPage
